import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/api";
import { Card, Btn, Spinner } from "@/components/ui-kit";
import { StatCard, SectionTitle, EmptyState, CampaignCard, ProComingSoon } from "@/components/dashboard-bits";
import { InstagramPromo } from "@/components/InstagramPromo";
import { PlusCircle, Percent, Megaphone, FileText, CheckCircle2 } from "lucide-react";

export default function BusinessDashboard() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/dashboard/business").then((r) => setData(r.data)); }, []);
  if (!data) return <Spinner />;

  return (
    <div className="space-y-10" data-testid="business-dashboard">
      <div className="rounded-3xl bg-[#D5E5F9] px-8 py-6 text-center" data-testid="publish-cta">
        <h2 className="text-2xl font-semibold tracking-display text-[#292827] leading-tight">Julkaise kampanja</h2>
        <p className="mt-2 text-[15px] text-[#3A3733] max-w-xl mx-auto" data-testid="publish-cta-description">Julkaise kampanja maksutta ja löydä tarpeisiisi sopivat sisällöntuottajat.</p>
        <ol className="mt-4 mx-auto w-fit max-w-full space-y-2 text-left text-[15px] text-[#292827]" data-testid="publish-cta-steps">
          {["Kerro, millaista sisältöä tai yhteistyötä etsit.", "Julkaise kampanja.", "Tutustu hakemuksiin ja valitse sopivat tekijät."].map((t, i) => (
            <li key={i} className="flex items-start gap-3">
              <span className="mt-px h-6 w-6 shrink-0 rounded-full bg-white text-[13px] font-semibold flex items-center justify-center">{i + 1}.</span>
              <span>{t}</span>
            </li>
          ))}
        </ol>
        <p className="mt-4 inline-block rounded-2xl sm:rounded-full bg-white/70 px-4 py-1.5 text-[14px] font-medium text-[#292827]" data-testid="publish-cta-free">Creatorin käyttö on toistaiseksi täysin ilmaista yrityksille!</p>
        <div className="mt-5 flex flex-col sm:flex-row gap-3 justify-center">
          <Btn to="/dashboard/julkaise" variant="secondary" data-testid="publish-cta-btn"><PlusCircle size={18} /> Julkaise kampanja</Btn>
          <Btn to="/dashboard/julkaise-affiliate" variant="dark" data-testid="publish-affiliate-cta-btn"><Percent size={18} /> Julkaise affiliate-ohjelma</Btn>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard label="Kampanjoita yhteensä" value={data.stats.total_campaigns} tone="accent" icon={Megaphone} to="/dashboard/omat-kampanjat" />
        <StatCard label="Avoimet kampanjat" value={data.stats.open_campaigns} tone="secondary" icon={CheckCircle2} to="/dashboard/omat-kampanjat" />
        <StatCard label="Hakemukset" value={data.stats.applications} tone="pink" icon={FileText} to="/dashboard/saapuneet-hakemukset" />
      </div>

      <InstagramPromo role="business" />

      <ProComingSoon features={[
        "Selaa Pro-sisällöntuottajien profiileja ja ota heihin yhteyttä suoraan ilman kampanjan luomista.",
        "Rajaton kampanjamäärä.",
        "Tulevat AI-ominaisuudet sisältyvät Pro-jäsenyyteen.",
      ]} />

      <div>
        <SectionTitle action={<Btn to="/dashboard/julkaise" variant="sm"><PlusCircle size={16} /> Julkaise</Btn>}>
          Aktiiviset kampanjat
        </SectionTitle>
        {data.my_campaigns.length === 0 ? (
          <EmptyState title="Et ole vielä julkaissut kampanjoita" subtitle="Julkaise ensimmäinen kampanjasi tavoittaaksesi sisällöntuottajat." />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {data.my_campaigns.map((c) => (
              <Link key={c.campaign_id} to="/dashboard/omat-kampanjat" className="block min-w-0"><CampaignCard c={c} /></Link>
            ))}
          </div>
        )}
      </div>

      <div>
        <SectionTitle action={<Btn to="/dashboard/saapuneet-hakemukset" variant="ghost" className="text-sm">Katso kaikki</Btn>}>
          Viimeisimmät hakemukset
        </SectionTitle>
        {data.recent_applications.length === 0 ? (
          <EmptyState title="Ei vielä hakemuksia" subtitle="Hakemukset kampanjoihisi näkyvät täällä." />
        ) : (
          <Card className="divide-y divide-[#E6E1D9]">
            {data.recent_applications.map((a) => (
              <Link key={a.application_id} to="/dashboard/saapuneet-hakemukset" className="block px-6 py-4 hover:bg-[#F5F3F0] transition-colors" data-testid="application-row">
                <p className="font-medium text-[#292827]">{a.creator_name}</p>
                <p className="text-sm text-[#968F85] mt-0.5">{a.campaign_title}</p>
              </Link>
            ))}
          </Card>
        )}
      </div>
    </div>
  );
}
